import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PincodeInput from "@/components/common/PincodeInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const EMPTY = {
  name: "", email: "", phone: "", home_address: "", working_location: "",
  working_radius_km: 25, kyc_aadhar: "", kyc_pan: "", working_agreement_notes: "",
  rate_per_report: 1750, city: "", pincode: "", manager_id: "",
};

function Field({ label, span = false, children }) {
  return <div className={span ? "md:col-span-2" : undefined}><Label>{label}</Label>{children}</div>;
}

const pincodeSummary = (m) => (Array.isArray(m.allowed_pincodes) && m.allowed_pincodes.length
  ? ` · ${m.allowed_pincodes.slice(0, 3).join(", ")}${m.allowed_pincodes.length > 3 ? `, +${m.allowed_pincodes.length - 3}` : ""}` : "");

function ManagerPicker({ value, onChange }) {
  const [managers, setManagers] = useState([]);
  useEffect(() => { api.get("/managers").then((r) => setManagers(r.data)).catch(() => {}); }, []);
  return (
    <Field label="Reporting Manager" span>
      <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
        <SelectTrigger data-testid="exec-manager-select"><SelectValue placeholder="Select a manager (optional)" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">— No manager (needs approval later) —</SelectItem>
          {managers.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`exec-manager-option-${m.id}`}>{m.name}{pincodeSummary(m)}</SelectItem>)}
        </SelectContent>
      </Select>
      <p className="text-[11px] text-slate-500 mt-1">
        If a manager is chosen, the executive is added directly to their team (auto-approved). Otherwise the manager whose territory matches will need to approve.
      </p>
    </Field>
  );
}

/* Shared "Add Field Executive" dialog for admin (manager picker + payment terms) and manager (auto-assigns to self). */
export default function ExecFormDialog({ onClose, onSaved, showManagerPicker = false, showPaymentTerms = false }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [pincodeOk, setPincodeOk] = useState(false);
  const set = (key, cast = (v) => v) => (e) => setForm((f) => ({ ...f, [key]: cast(e.target.value) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.working_location || (form.pincode && !pincodeOk)) { toast.error("Please enter a valid Rajasthan pincode"); return; }
    setBusy(true);
    try {
      const { manager_id, rate_per_report, working_agreement_notes, ...rest } = form;
      const body = showPaymentTerms ? { ...rest, rate_per_report, working_agreement_notes } : rest;
      await api.post("/executives", manager_id ? { ...body, manager_id } : body);
      toast.success("Executive added");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-heading">Add Field Executive</DialogTitle>
        <DialogDescription>{showPaymentTerms ? "Freelance executive personal, KYC and rate." : "Freelance executive personal and KYC details. Payment terms are set by the Admin."}</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Full Name"><Input required value={form.name} onChange={set("name")} data-testid="exec-name" /></Field>
        <Field label="Email"><Input type="email" required value={form.email} onChange={set("email")} data-testid="exec-email" /></Field>
        <Field label="Phone"><Input required value={form.phone} onChange={set("phone")} data-testid="exec-phone" /></Field>
        <PincodeInput
          value={form.pincode}
          onChange={(p) => { setForm((f) => ({ ...f, pincode: p })); setPincodeOk(false); }}
          onResolved={(r) => {
            setPincodeOk(r.isRajasthan);
            if (r.isRajasthan) setForm((f) => ({ ...f, pincode: r.pincode, city: r.city, working_location: r.district }));
          }}
          testId="exec-pincode"
          label="Working Pincode"
        />
        <Field label="City / Post Office"><Input value={form.city} readOnly className="bg-slate-50" data-testid="exec-city" /></Field>
        <Field label="Working District"><Input value={form.working_location} readOnly className="bg-slate-50" data-testid="exec-district" /></Field>
        <Field label="Working Radius (km)"><Input type="number" value={form.working_radius_km} onChange={set("working_radius_km", Number)} data-testid="exec-radius" /></Field>
        {showPaymentTerms && <Field label="Rate / Report (₹)"><Input type="number" value={form.rate_per_report} onChange={set("rate_per_report", Number)} data-testid="exec-rate" /></Field>}
        {showManagerPicker && <ManagerPicker value={form.manager_id} onChange={(v) => setForm((f) => ({ ...f, manager_id: v }))} />}
        <Field label="Home Address" span><Textarea required value={form.home_address} onChange={set("home_address")} data-testid="exec-home" /></Field>
        <Field label="Aadhaar"><Input value={form.kyc_aadhar} onChange={set("kyc_aadhar")} data-testid="exec-aadhar" /></Field>
        <Field label="PAN"><Input value={form.kyc_pan} onChange={set("kyc_pan")} data-testid="exec-pan" /></Field>
        {showPaymentTerms && <Field label="Working Agreement Notes" span><Textarea rows={2} value={form.working_agreement_notes} onChange={set("working_agreement_notes")} data-testid="exec-agreement" /></Field>}
        <DialogFooter className="md:col-span-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy} className="bg-slate-950 hover:bg-slate-800" data-testid="exec-save-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
